import {
  ChevronLeftSmall,
  ChevronRightSmall,
  ImageZoom,
} from '@components/icons'
import cn from 'clsx'
import { useKeenSlider } from 'keen-slider/react'
import Image from 'next/legacy/image'
import 'photoswipe/dist/photoswipe.css'
import React, {
  Children,
  FC,
  isValidElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Item } from 'react-photoswipe-gallery'
import { Product } from 'types/Product'
import s from './ProductSlider.module.css'

interface Props {
  images?: any[]
  key: string
  product: Product.Product
  children: ReactNode
  saleBadge: boolean
  isPromoOff: boolean
  isPremium?: boolean
}

const ProductSlider: FC<Props> = ({
  children,
  images,
  saleBadge,
  isPromoOff,
  isPremium,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isMounted, setIsMounted] = useState(false)
  const sliderContainerRef = useRef<HTMLDivElement>(null)
  const thumbsContainerRef = useRef<HTMLDivElement>(null)

  const [ref, slider] = useKeenSlider<HTMLDivElement>({
    loop: true,
    created: () => {
      setIsMounted(true)
    },
    slideChanged(s) {
      const slideNumber = s.track.details.rel
      setCurrentSlide(slideNumber)
      if (thumbsContainerRef.current) {
        const $el = document.getElementById(`thumb-${slideNumber}`)
        if (slideNumber >= 3) {
          thumbsContainerRef.current.scrollLeft = $el!.offsetLeft
        } else {
          thumbsContainerRef.current.scrollLeft = 0
        }
      }
    },
  })

  // Stop the history navigation gesture on touch devices
  useEffect(() => {
    const preventNavigation = (event: TouchEvent) => {
      // Center point of the touch area
      const touchXPosition = event.touches[0].pageX
      // Size of the touch area
      const touchXRadius = event.touches[0].radiusX || 0

      // We set a threshold (10px) on both sizes of the screen,
      // if the touch area overlaps with the screen edges
      // it's likely to trigger the navigation. We prevent the
      // touchstart event in that case.
      if (
        touchXPosition - touchXRadius < 10 ||
        touchXPosition + touchXRadius > window.innerWidth - 10
      )
        event.preventDefault()
    }

    const current = sliderContainerRef.current

    current!.addEventListener('touchstart', preventNavigation)

    return () => {
      if (current) {
        current!.removeEventListener('touchstart', preventNavigation)
      }
    }
  }, [])

  return (
    <>
      <div className="w-full flex direction-column flex-col-reverse sm:flex-row">
        <div className="hidden sm:flex">
          <div className={s.album} ref={thumbsContainerRef}>
            {isMounted &&
              slider.current &&
              Children.map(children, (child, idx) => {
                if (isValidElement(child)) {
                  return {
                    ...child,
                    props: {
                      ...child.props,
                      className: cn(child.props.className, s.thumb, {
                        [s.selected]: currentSlide === idx,
                      }),
                      id: `thumb-${idx}`,
                      onClick: () => {
                        slider.current?.moveToIdx(idx)
                      },
                    },
                  }
                }
                return child
              })}
          </div>
        </div>
        <div className={s.root} ref={sliderContainerRef}>
          {saleBadge && !isPremium && (
            <div
              className={cn(
                'absolute z-10 text-body3 font-bold  bg-brand-polar-white px-2 py-0.5 m-2 rounded-sm',
                isPromoOff ? 'text-promo-green' : 'text-white bg-ui-sale'
              )}
            >
              {isPromoOff ? 'PROMO' : 'SALE'}
            </div>
          )}
          <div
            ref={ref}
            className="keen-slider h-full transition-opacity duration-150"
            // style={{ opacity: isMounted ? 1 : 0 }}
          >
            <button
              className={cn(s.leftControl, s.control, {
                hidden: Children.toArray(children).length <= 1,
              })}
              onClick={slider.current?.prev}
              aria-label="Previous Product Image"
            >
              <ChevronLeftSmall height={32} width={32} />
            </button>
            <button
              className={cn(s.rightControl, s.control, {
                hidden: Children.toArray(children).length <= 1,
              })}
              onClick={slider.current?.next}
              aria-label="Next Product Image"
            >
              <ChevronRightSmall height={32} width={32} />
            </button>
            {images &&
              images?.map((image, index) => {
                return (
                  <Item
                    key={index}
                    original={image?.url!}
                    height={image.height}
                    width={image.width}
                  >
                    {({ ref, open }) => (
                      <div
                        className="keen-slider__slide pdp_image relative"
                        onClick={open}
                        ref={ref as React.MutableRefObject<HTMLDivElement>}
                        style={{ width: 794 }}
                      >
                        <Image
                          className={s.img}
                          src={image.url!}
                          alt={image.alt || 'Hoskings Product Image'}
                          width={794}
                          height={794}
                          objectFit="contain"
                          priority={index === 0}
                          quality="85"
                          unoptimized
                        />
                        <ImageZoom
                          className="absolute z-50 bottom-0 right-4"
                          onClick={open}
                        />
                      </div>
                    )}
                  </Item>
                )
              })}
            {!images &&
              Children.map(children, (child) => {
                // Add the keen-slider__slide className to children
                if (isValidElement(child)) {
                  return {
                    ...child,
                    props: {
                      ...child.props,
                      className: `${
                        child.props.className ? `${child.props.className} ` : ''
                      }keen-slider__slide`,
                    },
                  }
                }
                return child
              })}
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .pdp_image:hover {
            cursor: zoom-in;
          }
        `}
      </style>
    </>
  )
}

export default ProductSlider
